import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { accommodationColorPickerOptions } from "../theme/colors";

// --- FormLabel ---
export function FormLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <label className={clsx("form-label", className)}>{children}</label>;
}

// --- FormInput ---
export function FormInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  type = "text",
  placeholder,
  step,
  min,
  disabled,
  className,
  autoFocus,
  id,
  list,
  inputMode,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: {
  value: string | number;
  onChange: (val: string) => void;
  /** For fields that commit on blur rather than on every keystroke. */
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  step?: string;
  min?: string;
  disabled?: boolean;
  className?: string;
  /** Marks the field as the modal's initial focus target */
  autoFocus?: boolean;
  id?: string;
  /** Id of a <datalist> offering suggestions. */
  list?: string;
  inputMode?: "text" | "url" | "numeric" | "decimal";
  "aria-label"?: string;
  "data-testid"?: string;
}) {
  return (
    <input
      id={id}
      list={list}
      inputMode={inputMode}
      aria-label={ariaLabel}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      step={step}
      min={min}
      disabled={disabled}
      data-autofocus={autoFocus ? "" : undefined}
      data-testid={dataTestId}
      className={clsx(
        "input-base",
        type === "date" && "min-w-0 pr-10 tabular-nums",
        className,
      )}
    />
  );
}

// --- FormTextarea ---
interface FormTextareaProps {
  value: string;
  onChange: (val: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  /** Grow with the content instead of scrolling inside a fixed height. */
  autoGrow?: boolean;
  /** Marks the field as the modal's initial focus target */
  autoFocus?: boolean;
  id?: string;
  "aria-label"?: string;
  className?: string;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  function FormTextarea(
    {
      value,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      rows = 3,
      disabled,
      autoGrow = false,
      autoFocus,
      id,
      "aria-label": ariaLabel,
      className,
    },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    useLayoutEffect(() => {
      const textarea = innerRef.current;
      if (!autoGrow || !textarea) return;
      // Runtime geometry: collapse, then size to the content's scroll height.
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight + 4}px`;
    }, [autoGrow, value]);

    return (
      <textarea
        ref={innerRef}
        id={id}
        data-autofocus={autoFocus ? "" : undefined}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={clsx(
          "input-base",
          rows > 1 && "min-h-[60px]",
          autoGrow ? "resize-none overflow-hidden leading-relaxed" : "resize-y",
          className,
        )}
      />
    );
  },
);

// --- EditorTitleInput ---
/**
 * The name at the top of an editor, styled as its heading. Long names (flight
 * numbers plus a route) wrap instead of scrolling sideways, and Enter doesn't
 * add a line break.
 */
export function EditorTitleInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  return (
    <FormTextarea
      value={value}
      onChange={(next) => onChange(next.replace(/[\r\n]+/g, " "))}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
      aria-label="Name"
      placeholder={placeholder}
      disabled={disabled}
      rows={1}
      autoFocus={autoFocus}
      autoGrow
      className="-mx-2 w-[calc(100%+1rem)] bg-transparent px-2 text-lg font-bold leading-snug hover:bg-subtle focus:bg-surface"
    />
  );
}

// --- FormSelect ---
export function FormSelect({
  value,
  onChange,
  options,
  disabled,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  "data-testid"?: string;
  "aria-label"?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      className="select-base pr-8"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// --- FormRow ---
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3 sm:flex-row">{children}</div>;
}

// --- FormField (wrapper with label) ---
export function FormField({
  label,
  children,
  flex,
}: {
  label: string;
  children: ReactNode;
  flex?: number;
}) {
  return (
    <div style={{ flex }} className="min-w-0">
      <FormLabel>{label}</FormLabel>
      {children}
    </div>
  );
}

// --- TagInput ---
export function TagInput({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput("");
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Type and press Enter..."
          className="input-base flex-1"
        />
      </div>
      {tags.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {tags.map((tag) => (
            <span key={tag} className="tag py-[3px]">
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="flex items-center bg-transparent border-none cursor-pointer p-0 text-tertiary text-sm leading-none hover:text-primary"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// --- DayOfWeekCheckboxes ---
const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DayOfWeekCheckboxes({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (days: string[]) => void;
}) {
  const toggle = (day: string) => {
    if (selected.includes(day)) {
      onChange(selected.filter((d) => d !== day));
    } else {
      onChange([...selected, day]);
    }
  };

  return (
    <div className="flex gap-1">
      {DAYS_OF_WEEK.map((day, i) => {
        const active = selected.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => toggle(day)}
            className={clsx(
              "flex-1 py-1.5 text-xs font-semibold font-sans rounded-md cursor-pointer transition-all",
              active
                ? "border border-primary bg-primary text-white"
                : "border border-border bg-white text-secondary hover:bg-gray-50",
            )}
          >
            {DAY_LABELS[i]}
          </button>
        );
      })}
    </div>
  );
}

// --- ColorPicker ---
export interface ColorPickerOption {
  index: number;
  bg: string;
  ring: string;
}

export function ColorPicker({
  selectedIndex,
  onChange,
  options = accommodationColorPickerOptions,
  ariaLabel,
}: {
  selectedIndex: number;
  onChange: (index: number) => void;
  /** Defaults to the accommodation palette; legs pass their own. */
  options?: readonly ColorPickerOption[];
  ariaLabel?: string;
}) {
  return (
    <div className="flex gap-2">
      {options.map((c) => (
        <button
          key={c.index}
          type="button"
          aria-label={
            ariaLabel ? `${ariaLabel} ${c.index + 1}` : `Colour ${c.index + 1}`
          }
          aria-pressed={selectedIndex === c.index}
          onClick={() => onChange(c.index)}
          className="size-8 rounded-full cursor-pointer transition-all hover:scale-110"
          style={{
            background: c.bg,
            border:
              selectedIndex === c.index
                ? `3px solid ${c.ring}`
                : "2px solid var(--color-border)",
            boxShadow:
              selectedIndex === c.index
                ? `0 0 0 2px white, 0 0 0 4px ${c.ring}`
                : "none",
          }}
        />
      ))}
    </div>
  );
}

// --- FormCheckbox ---
/** Compact labelled checkbox for secondary on/off settings. */
export function FormCheckbox({
  checked,
  onChange,
  label,
  icon,
  title,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Material Symbols glyph shown before the label. */
  icon?: string;
  /** Longer explanation, shown on hover. */
  title?: string;
  disabled?: boolean;
}) {
  return (
    <label
      title={title}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-md text-sm text-secondary has-[:checked]:font-semibold has-[:checked]:text-primary has-[:disabled]:cursor-default has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-info"
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 cursor-pointer accent-[var(--color-primary)]"
      />
      {icon && (
        <span
          className="material-symbols-outlined icon-match-text text-tertiary"
          aria-hidden
        >
          {icon}
        </span>
      )}
      {label}
    </label>
  );
}

// --- Section Toggle ---
export function FormSection({
  label,
  enabled,
  onToggle,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <div
        className={clsx(
          "flex items-center justify-between",
          enabled && "mb-2.5",
        )}
      >
        <FormLabel className="!mb-0">{label}</FormLabel>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={label}
          onClick={() => onToggle(!enabled)}
          className={clsx(
            "relative w-9 h-5 rounded-full border-none cursor-pointer transition-colors p-0",
            enabled ? "bg-primary" : "bg-border",
          )}
        >
          <div
            className={clsx(
              "absolute top-0.5 size-4 rounded-full bg-white transition-[left]",
              enabled ? "left-[18px]" : "left-0.5",
            )}
          />
        </button>
      </div>
      {enabled && children}
    </div>
  );
}
